import React, { useState, useRef } from 'react';
import { useUserResumes, useDeleteResume, useUploadResume, useUpdateResumeLabel } from '../../hooks/unified/useResumeQueries';
import { LoadingSpinner } from '../ui/LoadingSpinner';
import { useToast } from '../../contexts/ToastContext';

interface ResumeManagementCardProps {
  onResumeUploaded?: (resume: any) => void;
  onResumeDeleted?: (resumeId: string) => void;
}

export const ResumeManagementCard: React.FC<ResumeManagementCardProps> = ({
  onResumeUploaded,
  onResumeDeleted,
}) => {
  // Fetch user resumes
  const { data: resumes, isLoading, error } = useUserResumes();
  const deleteResumeMutation = useDeleteResume();
  const uploadResumeMutation = useUploadResume();
  const updateLabelMutation = useUpdateResumeLabel();
  const { showSuccess, showError } = useToast();

  // Resume limit
  const MAX_RESUMES = 5;
  const resumeCount = resumes?.length || 0;
  const isLimitReached = resumeCount >= MAX_RESUMES;

  // Upload state
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Delete confirmation state
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);

  // Label editing state
  const [editingLabelId, setEditingLabelId] = useState<string | null>(null);
  const [editLabelValue, setEditLabelValue] = useState('');

  // Format date for display
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  // Get status badge color
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'scanned':
        return 'bg-green-100 text-green-800';
      case 'processing':
        return 'bg-blue-100 text-blue-800';
      case 'submitted':
        return 'bg-yellow-100 text-yellow-800';
      case 'error':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  // Find most recent resume
  const getMostRecentResumeId = () => {
    if (!resumes || resumes.length === 0) return null;
    const sorted = [...resumes].sort(
      (a, b) => new Date(b.submitted_at || '').getTime() - new Date(a.submitted_at || '').getTime()
    );
    return sorted[0]?.id;
  };

  const mostRecentId = getMostRecentResumeId();

  // Handle file selection
  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      handleUpload(file);
    }
  };

  // Handle upload
  const handleUpload = async (file: File) => {
    // Check resume limit
    if (isLimitReached) {
      showError(
        'Resume Limit Reached',
        `You can upload a maximum of ${MAX_RESUMES} resumes. Please delete an existing resume before uploading a new one.`
      );
      return;
    }

    // Validate file type
    if (file.type !== 'application/pdf') {
      showError('Invalid File Type', 'Only PDF files are accepted.');
      return;
    }

    // Validate file size (10MB)
    const maxSize = 10 * 1024 * 1024;
    if (file.size > maxSize) {
      showError('File Too Large', 'File size must be less than 10MB.');
      return;
    }

    setIsUploading(true);
    setUploadProgress(0);

    try {
      const result = await uploadResumeMutation.mutateAsync({
        file,
        onProgress: (progress) => {
          setUploadProgress(progress);
        },
      });

      showSuccess('Resume Uploaded', 'Your resume has been uploaded successfully.');
      setUploadProgress(0);
      
      if (onResumeUploaded) {
        onResumeUploaded(result);
      }
    } catch (err: any) {
      showError('Upload Failed', err.message || 'Failed to upload resume. Please try again.');
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  // Trigger file input
  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  // Handle delete confirmation
  const handleDeleteClick = (resumeId: string) => {
    setDeleteConfirmId(resumeId);
  };

  // Handle delete confirm
  const handleDeleteConfirm = async () => {
    if (!deleteConfirmId) return;

    try {
      await deleteResumeMutation.mutateAsync(deleteConfirmId);
      showSuccess('Resume Deleted', 'Your resume has been deleted successfully.');
      
      if (onResumeDeleted) {
        onResumeDeleted(deleteConfirmId);
      }
    } catch (err: any) {
      if (err.message?.includes('active applications')) {
        showError(
          'Cannot Delete Resume',
          'This resume is attached to active job applications and cannot be deleted.'
        );
      } else {
        showError('Delete Failed', err.message || 'Failed to delete resume. Please try again.');
      }
    } finally {
      setDeleteConfirmId(null);
    }
  };

  // Handle delete cancel
  const handleDeleteCancel = () => {
    setDeleteConfirmId(null);
  };

  // Handle label edit start
  const handleEditLabel = (resumeId: string, currentLabel: string | null | undefined) => {
    setEditingLabelId(resumeId);
    setEditLabelValue(currentLabel || '');
  };

  // Handle label save
  const handleSaveLabel = async (resumeId: string) => {
    try {
      await updateLabelMutation.mutateAsync({ id: resumeId, label: editLabelValue.toUpperCase() });
      showSuccess('Label Updated', 'Resume label has been updated.');
      setEditingLabelId(null);
      setEditLabelValue('');
    } catch (err: any) {
      showError('Update Failed', err.message || 'Failed to update label.');
    }
  };

  // Handle label cancel
  const handleCancelLabel = () => {
    setEditingLabelId(null);
    setEditLabelValue('');
  };

  return (
    <div className="bg-white shadow rounded-lg p-6">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Resume Management</h2>
          <p className="text-sm text-gray-600 mt-1">
            {resumeCount} of {MAX_RESUMES} resumes uploaded
            {isLimitReached && (
              <span className="ml-2 text-amber-600 font-medium">
                (Limit reached - delete a resume to upload new one)
              </span>
            )}
          </p>
        </div>
        
        {/* Upload Section */}
        <div className="flex items-center gap-2">
          <button
            onClick={handleUploadClick}
            disabled={isUploading || isLimitReached}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
            title={isLimitReached ? `Maximum ${MAX_RESUMES} resumes allowed` : 'Upload a new resume'}
          >
            {isUploading ? (
              <>
                <LoadingSpinner size="sm" className="mr-2" />
                Uploading...
              </>
            ) : (
              <>
                <svg
                  className="mr-2 h-4 w-4"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                  />
                </svg>
                Upload Resume
              </>
            )}
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept=".pdf,application/pdf"
            onChange={handleFileSelect}
            className="hidden"
          />
        </div>
      </div>

      {/* Upload Progress */}
      {isUploading && uploadProgress > 0 && (
        <div className="mb-4">
          <div className="flex items-center justify-between text-sm text-gray-600 mb-1">
            <span>Uploading...</span>
            <span>{uploadProgress}%</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div
              className="bg-blue-600 h-2 rounded-full transition-all duration-300"
              style={{ width: `${uploadProgress}%` }}
            />
          </div>
        </div>
      )}
      
      {/* Loading State */}
      {isLoading && (
        <div className="flex justify-center py-8">
          <LoadingSpinner size="md" />
        </div>
      )}

      {/* Error State */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <p className="text-sm text-red-800">
            Failed to load resumes. Please try again.
          </p>
        </div>
      )}

      {/* Empty State */}
      {!isLoading && !error && (!resumes || resumes.length === 0) && (
        <div className="text-center py-8">
          <svg
            className="mx-auto h-12 w-12 text-gray-400"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
            />
          </svg>
          <h3 className="mt-2 text-sm font-medium text-gray-900">No resumes</h3>
          <p className="mt-1 text-sm text-gray-500">
            Get started by uploading your first resume.
          </p>
        </div>
      )}

      {/* Resume List */}
      {!isLoading && !error && resumes && resumes.length > 0 && (
        <div className="space-y-3">
          {resumes.map((resume) => (
            <div
              key={resume.id}
              className={`border rounded-lg p-4 ${
                resume.id === mostRecentId
                  ? 'border-primary-500 bg-primary-50'
                  : 'border-gray-200'
              }`}
            >
              <div className="flex items-start justify-between">
                <div className="flex-1 min-w-0">
                  {/* Filename */}
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="text-sm font-medium text-gray-900 truncate">
                      {resume.filename}
                    </h3>
                    {resume.id === mostRecentId && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-primary-100 text-primary-800">
                        Most Recent
                      </span>
                    )}
                  </div>
                  
                  {/* Editable Label */}
                  <div className="mb-2">
                    {editingLabelId === resume.id ? (
                      <div className="flex items-center gap-2">
                        <input
                          type="text"
                          value={editLabelValue}
                          onChange={(e) => setEditLabelValue(e.target.value.toUpperCase())}
                          placeholder="ENTER LABEL"
                          className="px-2 py-1 text-xs font-medium uppercase border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-primary-500 w-40"
                          autoFocus
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') handleSaveLabel(resume.id);
                            if (e.key === 'Escape') handleCancelLabel();
                          }}
                        />
                        <button
                          onClick={() => handleSaveLabel(resume.id)}
                          disabled={updateLabelMutation.isPending}
                          className="text-xs text-primary-600 hover:text-primary-700 font-medium"
                        >
                          {updateLabelMutation.isPending ? '...' : 'Save'}
                        </button>
                        <button
                          onClick={handleCancelLabel}
                          className="text-xs text-gray-500 hover:text-gray-700"
                        >
                          Cancel
                        </button>
                      </div>
                    ) : (
                      <button
                        onClick={() => handleEditLabel(resume.id, resume.label)}
                        className="group flex items-center gap-1 text-xs text-gray-500 hover:text-primary-600"
                      >
                        {resume.label ? (
                          <span className="font-medium text-gray-700 uppercase">{resume.label}</span>
                        ) : (
                          <span className="italic">Add label</span>
                        )}
                        <svg className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                        </svg>
                      </button>
                    )}
                  </div>
                  
                  <div className="flex items-center gap-3 text-xs text-gray-500">
                    <span>Uploaded: {resume.submitted_at ? formatDate(resume.submitted_at) : 'N/A'}</span>
                    <span className={`inline-flex items-center px-2 py-0.5 rounded ${getStatusColor(resume.status)}`}>
                      {resume.status}
                    </span>
                  </div>
                </div>
                
                <div className="ml-4 flex-shrink-0">
                  <button
                    onClick={() => handleDeleteClick(resume.id)}
                    disabled={deleteResumeMutation.isPending}
                    className="inline-flex items-center px-3 py-1.5 border border-red-300 text-sm font-medium rounded-md text-red-700 bg-white hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <svg
                      className="h-4 w-4 mr-1"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                      />
                    </svg>
                    Delete
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      {deleteConfirmId && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <div className="flex items-center mb-4">
              <div className="flex-shrink-0">
                <svg
                  className="h-6 w-6 text-red-600"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                  />
                </svg>
              </div>
              <h3 className="ml-3 text-lg font-medium text-gray-900">
                Delete Resume
              </h3>
            </div>
            <p className="text-sm text-gray-500 mb-6">
              Are you sure you want to delete this resume? This action cannot be undone.
              If this resume is attached to active applications, it cannot be deleted.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={handleDeleteCancel}
                disabled={deleteResumeMutation.isPending}
                className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteConfirm}
                disabled={deleteResumeMutation.isPending}
                className="px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50"
              >
                {deleteResumeMutation.isPending ? (
                  <>
                    <LoadingSpinner size="sm" className="inline mr-2" />
                    Deleting...
                  </>
                ) : (
                  'Delete'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
